import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Modal,
  TextInput,
  Text,
  Group,
  Box,
  Badge,
  ScrollArea,
  Stack,
  UnstyledButton,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { GloveboxDocType, GloveboxDocCategory } from "@/utils/api";
import { useAppEssentialsContext } from "@/contexts";
import { getCategoryColor, getDocTypeIcon } from "@/utils/gloveboxHelpers";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface GloveboxDocTypePickerModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Callback to close the modal */
  onClose: () => void;

  /** Currently selected document type ID */
  value: number | null;

  /** Callback when a doc type is selected */
  onSelect: (
    docTypeId: number | null,
    docType?: GloveboxDocType
  ) => void;

  /** Filter by category (vehicle, driver, equipment, other) */
  category?: GloveboxDocCategory | GloveboxDocCategory[];

  /** Custom filter function to filter document types */
  docTypeFilter?: (docType: GloveboxDocType) => boolean;

  /** Whether to show category badge in options */
  showCategoryBadge?: boolean;

  /** Whether to group options by category */
  groupByCategory?: boolean;
}

// ============================================
// Constants
// ============================================

const ICON_SIZES = {
  search: 20,
  categoryHeader: 18,
  optionIcon: 28,
  selectedCheck: 18,
};

const FUSE_OPTIONS: IFuseOptions<GloveboxDocType> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "code", weight: 0.2 },
    { name: "description", weight: 0.1 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Component
// ============================================

export const GloveboxDocTypePickerModal: React.FC<GloveboxDocTypePickerModalProps> = ({
  opened,
  onClose,
  value,
  onSelect,
  category,
  docTypeFilter,
  showCategoryBadge = false,
  groupByCategory = false,
}) => {
  const { t } = useTranslation(["glovebox", "common"]);
  const { appEssentials } = useAppEssentialsContext();

  const docTypes: GloveboxDocType[] = appEssentials?.gloveboxDocTypes || [];

  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (!opened) {
      setSearchValue("");
    }
  }, [opened]);

  // Filter document types by category and custom filter
  const filteredDocTypes = useMemo(() => {
    let filtered = docTypes;

    if (category) {
      const categories = Array.isArray(category) ? category : [category];
      filtered = filtered.filter((dt) =>
        categories.includes(dt.category as GloveboxDocCategory)
      );
    }

    if (docTypeFilter) {
      filtered = filtered.filter(docTypeFilter);
    }

    return [...filtered].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
  }, [docTypes, category, docTypeFilter]);

  // Build Fuse index for fuzzy search
  const fuseIndex = useMemo(
    () => new Fuse(filteredDocTypes, FUSE_OPTIONS),
    [filteredDocTypes]
  );

  // Apply fuzzy search filtering
  const searchedDocTypes = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return filteredDocTypes;
    return fuseIndex.search(search).map((result) => result.item);
  }, [filteredDocTypes, searchValue, fuseIndex]);

  // Group searched doc types by category
  const groupedDocTypes = useMemo(() => {
    if (!groupByCategory) return null;

    const groupMap = new Map<string, GloveboxDocType[]>();

    searchedDocTypes.forEach((docType) => {
      const cat = docType.category || "other";
      if (!groupMap.has(cat)) {
        groupMap.set(cat, []);
      }
      groupMap.get(cat)!.push(docType);
    });

    const groups: { category: string; label: string; items: GloveboxDocType[] }[] = [];
    groupMap.forEach((items, cat) => {
      if (items.length > 0) {
        groups.push({
          category: cat,
          label: t(`glovebox:categories.${cat}`),
          items,
        });
      }
    });

    return groups;
  }, [searchedDocTypes, groupByCategory, t]);

  // Handle doc type selection
  const handleSelect = useCallback(
    (docType: GloveboxDocType) => {
      if (docType.id !== undefined) {
        onSelect(docType.id, docType);
        onClose();
      }
    },
    [onSelect, onClose]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.currentTarget.value);
    },
    []
  );

  // Handle clearing search
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    searchInputRef.current?.focus();
  }, []);

  const hasResults = searchedDocTypes.length > 0;

  // Render a single doc type row
  const renderDocTypeRow = (docType: GloveboxDocType) => {
    const isSelected = value === docType.id;

    return (
      <UnstyledButton
        key={docType.id}
        onClick={() => handleSelect(docType)}
        w="100%"
        px="md"
        py="sm"
        style={(theme) => ({
          backgroundColor: isSelected
            ? `var(--mantine-color-${theme.primaryColor}-0)`
            : undefined,
          borderBottom: "1px solid var(--mantine-color-gray-1)",
          "&:active": {
            backgroundColor: "var(--mantine-color-gray-1)",
          },
        })}
      >
        <Group gap="sm" wrap="nowrap">
          <AppIcon
            iconName={getDocTypeIcon(docType.code, docType.category)}
            size={ICON_SIZES.optionIcon}
          />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={isSelected ? 600 : 500}>
              {docType.name || "Unknown"}
            </Text>
            {docType.description && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                {docType.description}
              </Text>
            )}
          </Box>

          {showCategoryBadge && (
            <Badge
              size="xs"
              variant="light"
              color={getCategoryColor(docType.category)}
              style={{ flexShrink: 0 }}
            >
              {t(`glovebox:categories.${docType.category || "other"}`)}
            </Badge>
          )}

          {isSelected && (
            <Box style={{ flexShrink: 0 }}>
              <AppIcon
                iconName="Check"
                size={ICON_SIZES.selectedCheck}
                color="var(--mantine-color-blue-6)"
              />
            </Box>
          )}
        </Group>
      </UnstyledButton>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={t("glovebox:selectors.docType.modalTitle", {
        defaultValue: t("glovebox:selectors.docType.label"),
      })}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100dvh - 60px)",
          padding: 0,
          overflow: "hidden",
        },
        header: {
          padding: "12px 16px",
        },
        content: {
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        px="md"
        py="sm"
        style={{
          flexShrink: 0,
          borderBottom: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <TextInput
          ref={searchInputRef}
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t("glovebox:selectors.docType.searchPlaceholder", {
            defaultValue: t("glovebox:selectors.docType.placeholder"),
          })}
          leftSection={<AppIcon iconName="Search" size={ICON_SIZES.search} />}
          rightSection={
            searchValue ? (
              <CloseButton
                size="sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label={t("common:form.clearSearch")}
              />
            ) : undefined
          }
          size="md"
          autoComplete="off"
        />
      </Box>

      {/* Scrollable Options List */}
      <ScrollArea
        style={{ flex: 1, minHeight: 0 }}
        type="scroll"
        offsetScrollbars
      >
        <Stack gap={0} pb="md">
          {/* No results message */}
          {!hasResults && searchValue && (
            <Box px="md" py="xl">
              <Text c="dimmed" ta="center" size="sm">
                {t("common:form.noOptions")}
              </Text>
            </Box>
          )}

          {/* Grouped options */}
          {groupByCategory && groupedDocTypes
            ? groupedDocTypes.map((group) => (
              <Box key={group.category}>
                {/* Category Header */}
                <Group
                  gap="xs"
                  px="md"
                  py="xs"
                  bg="gray.0"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <AppIcon
                    iconName="Folder"
                    size={ICON_SIZES.categoryHeader}
                  />
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    {group.label}
                  </Text>
                </Group>

                {group.items.map(renderDocTypeRow)}
              </Box>
            ))
            : /* Flat list */
            searchedDocTypes.map(renderDocTypeRow)}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};